import { memo, useRef, useState, useCallback } from 'react';

import { FiPlay, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { FaPlay } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import getVideoId from 'get-video-id';
import { UseQueryResult } from 'react-query';
import { AxiosError } from 'axios';

import { VideosData, VideosResult } from '@common/types/api';

import * as S from './styles';

type VideosCarouselProps = {
  videosQuery: UseQueryResult<VideosResult, AxiosError>;
};

const getVideoThumbnail = (video: VideosData) => {
  const { id, service } = getVideoId(video.url_video);
  return service === 'youtube'
    ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
    : video.url_image;
};

const VideoCardItem = ({
  video,
  index,
}: {
  video: VideosData;
  index: number;
}) => {
  const thumbnail = getVideoThumbnail(video);

  return (
    <S.VideoCard $index={index}>
      <Link to={`/videos/${video.id}`}>
        <S.VideoThumb $index={index}>
          {thumbnail && <img src={thumbnail} alt={video.title} />}
          <S.PlayOverlay className="play-overlay">
            <FaPlay size={16} />
          </S.PlayOverlay>
        </S.VideoThumb>
        <S.VideoInfo>
          <h3>{video.title}</h3>
        </S.VideoInfo>
      </Link>
    </S.VideoCard>
  );
};

const VideosCarousel = ({ videosQuery }: VideosCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const { t } = useTranslation();

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const firstCard = el.querySelector(':scope > *') as HTMLElement;
    const cardW = firstCard ? firstCard.offsetWidth : 280;
    const amount = cardW + 16;
    el.scrollBy({
      left: dir === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
    setTimeout(checkScroll, 350);
  };

  if (!videosQuery.isSuccess || !videosQuery.data?.videos?.length) {
    return null;
  }

  const { videos } = videosQuery.data;

  return (
    <div style={{ marginBottom: '2.95rem' }}>
      <S.Header>
        <S.HeaderLeft>
          <FiPlay size={20} />
          <h2>{t('pages.videos.title')}</h2>
          {/* <S.CountBadge>
            {videos.length} {t('pages.videos.available') || 'disponíveis'}
          </S.CountBadge> */}
        </S.HeaderLeft>
        {videos.length > 3 && (
          <S.ShowAllLink to="/videos/1">{t('pages.videos.all')}</S.ShowAllLink>
        )}
      </S.Header>

      <S.CarouselWrapper>
        {canLeft && (
          <S.Arrow $direction="left" onClick={() => scroll('left')}>
            <FiChevronLeft size={14} />
          </S.Arrow>
        )}
        {canRight && videos.length > 3 && (
          <S.Arrow $direction="right" onClick={() => scroll('right')}>
            <FiChevronRight size={14} />
          </S.Arrow>
        )}

        <S.ScrollContainer ref={scrollRef} onScroll={checkScroll}>
          {videos.map((video, index) => (
            <VideoCardItem key={video.id} video={video} index={index} />
          ))}
        </S.ScrollContainer>
      </S.CarouselWrapper>
    </div>
  );
};

export default memo(VideosCarousel);
